import React, { useState, useEffect, FunctionComponent } from 'react'
import FilteredItemList, { DetailItem } from './FilteredItemList'
import { Form, InputGroup, Button, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

type Category = {
  value: string
  text: string
}

export interface ListingProps {
  heading: string
  categories: Category[]
  initialDetailItems: DetailItem[]
  type: number
  noHitsText: string
  showOnlyOpenAreaText: string
  firstCategoryOptionText: string
  searchHelpText: string
  searchLabelText: string
  producedByAnimalRawText: string
}

const Listing: FunctionComponent<ListingProps> = ({
  heading,
  categories,
  initialDetailItems,
  type,
  noHitsText,
  showOnlyOpenAreaText,
  firstCategoryOptionText,
  searchHelpText,
  searchLabelText,
  producedByAnimalRawText,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [detailItems, setDetailItems] = useState<DetailItem[]>(initialDetailItems || [])
  const [filteredItems, setFilteredItems] = useState<DetailItem[]>(initialDetailItems)
  const [onlyOpenArea, setOnlyOpenArea] = useState(false)
  const [producedByAnimal, setProducedByAnimal] = useState(false)
  const { t } = useTranslation()
  const noFilteredHitsText = noHitsText.replace('{0}', `<i class="search-term">${searchTerm}</i>`)

  const normalize = (str: string) =>
    str
      .toLowerCase()
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .replace(/e\s+(\d+)/g, 'e$1') // remove space(s) between 'e' and digits only

  useEffect(() => {
    let filtered = [...detailItems]

    if (searchTerm) {
      const normalizedSearchTerm = normalize(searchTerm)

      filtered = filtered.filter((item) => {
        const searchable = `${item.title} ${item.name} ${item.category}`
        return normalize(searchable).includes(normalizedSearchTerm)
      })
    }

    if (selectedCategory) {
      filtered = filtered.filter((item) => item.category.includes(selectedCategory))
    }

    if (onlyOpenArea) {
      filtered = filtered.filter((item) => item.category.includes('Öppet'))
    }

    if (producedByAnimal) {
      filtered = filtered.filter((item) => item.animalRaw === true)
    }

    // SORT
    const extractLeadingNumber = (title: string): number | null => {
      let match = title.match(/E\s*(\d+)/i)
      if (match) return parseInt(match[1], 10)

      match = title.match(/^(\d+)\s*[-–]?\s*/)
      if (match) return parseInt(match[1], 10)

      return null
    }

    filtered.sort((a, b) => {
      const aNum = extractLeadingNumber(a.title)
      const bNum = extractLeadingNumber(b.title)

      if (aNum !== null && bNum !== null) return aNum - bNum
      if (aNum !== null) return -1
      if (bNum !== null) return 1

      return a.title.localeCompare(b.title)
    })

    setFilteredItems(filtered)
  }, [searchTerm, selectedCategory, onlyOpenArea, producedByAnimal, detailItems])

  return (
    <div id="faq">
      <Row>
        <div className="search-and-filter clearfix mb-4">
          <h2>{heading}</h2>

          <Form.Group className="mb-3">
            <Form.Label className="mb-2 h4 w-100" htmlFor="text-filter">
              {searchLabelText}:
            </Form.Label>
            <InputGroup>
              <Form.Control
                id="text-filter"
                type="search"
                name="query"
                placeholder={searchHelpText}
                aria-label="Sökformulär"
                aria-describedby="id-search-button"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="primary" id="id-search-button" className="forums__search-button">
                <i className="fa fa-search me-2" />
                {t('search')}
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group controlId="categoryselect" className="d-flex flex-column mb-3 mt-3">
            <Form.Label className="mb-2 h4">{firstCategoryOptionText}:</Form.Label>
            <Form.Select className="category-filter" onChange={(e) => setSelectedCategory(e.target.value)}>
              <option value="">{firstCategoryOptionText}</option>
              {categories.map((cat, idx) => (
                <option key={idx} value={cat.value}>
                  {cat.text}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {showOnlyOpenAreaText && (
            <Form.Check
              type="checkbox"
              id="seaarea-open"
              label={showOnlyOpenAreaText}
              checked={onlyOpenArea}
              onChange={() => setOnlyOpenArea(!onlyOpenArea)}
              className="mb-2"
            />
          )}

          {producedByAnimalRawText && (
            <Form.Check
              type="checkbox"
              id="produced-by-animal"
              label={producedByAnimalRawText}
              checked={producedByAnimal}
              onChange={() => setProducedByAnimal(!producedByAnimal)}
            />
          )}
        </div>
      </Row>
      {filteredItems && <FilteredItemList items={filteredItems} type={type} noFilteredHitsText={noFilteredHitsText} />}
    </div>
  )
}

export default Listing
